import { useActiveLang } from "../../app/hooks/useActiveLang";
import {
  seedApiQueryData,
  useApiInfiniteQuery,
  useRetryApiInfiniteQuery,
  type Api,
} from "../../../services/api";

/** Playlist model as received from API (JSON-serialized, dates are `string`). */
export type ApiPlaylistModel =
  Api["get"]["/webapp/public/playlists/:playlistId"]["output"]["body"];

/**
 * Seed the playlist-detail query cache from a recommended-list payload before
 * navigating to the playlist page. The recommended endpoint runs the same
 * `populatePlaylists` pipeline as the detail endpoint, so a list doc IS the full
 * detail shape (`ApiPlaylistModel`) — the page's header renders instantly from
 * the seed while react-query refetches the authoritative detail in the
 * background. Argument typed as `ApiPlaylistModel` so a partial/feed playlist
 * shape (missing description, summary, content ids…) is rejected at compile
 * time and simply isn't seeded.
 */
export function seedPlaylistDetailCache(playlist: ApiPlaylistModel): void {
  seedApiQueryData(
    "/webapp/public/playlists/:playlistId",
    { param: { playlistId: playlist.id } },
    { status: 200, headers: {}, body: playlist }
  );
}

const limit = 20;

export function useGetRecommendedPlaylistsQuery(
  roomId: string | null,
  params: {
    medicalSpecialties?: string;
    tags?: string;
  } = {}
) {
  const { contentLang } = useActiveLang();

  const query = useApiInfiniteQuery("/webapp/public/playlists/recommended", {
    durable: true,
    query: {
      limit,
      language: contentLang,
      medical_specialties:
        params.medicalSpecialties !== undefined &&
        params.medicalSpecialties !== ""
          ? params.medicalSpecialties.split(",")
          : undefined,
      tags:
        params.tags !== undefined && params.tags !== ""
          ? params.tags.split(",")
          : undefined,
      roomId: roomId ?? undefined,
    },
  });

  const playlists = query.state === "success" ? query.docs : [];
  const playlistIds = playlists.map((playlist) => playlist.id).join(",");

  return {
    playlists,
    playlistIds,
    isLoading: query.state === "pending",
    isFetching: query.isFetching,
    fetchNextPage:
      query.state === "success" ? query.fetchNextPage : () => void 0,
    canFetchNextPage: query.state === "success" && query.canFetchNextPage,
  };
}

/**
 * Personalized counterpart of `useGetRecommendedPlaylistsQuery`, for the
 * surfaces outside room/company mode: the Discovery "Collections for you" rail
 * and the full list behind its "See all". Ranks the curated corpus against the
 * CALLER's MeSH profile (learned + declared topics + followed rooms +
 * specialty) via the same engine as the for-you feed.
 *
 * Returns an empty list when nothing in the corpus matches — callers show an
 * empty state or hide their section rather than fall back to unrelated
 * collections. Same return shape as `useGetRecommendedPlaylistsQuery` so a
 * caller can pick between the two at runtime.
 */
export function useGetPersonalRecommendedPlaylistsQuery(
  topicUid: string | null
) {
  const { contentLang } = useActiveLang();

  const query = useApiInfiniteQuery("/webapp/private/playlists/recommended", {
    durable: true,
    query: {
      limit,
      language: contentLang,
      ...(topicUid === null ? {} : { topicUid }),
    },
  });

  const retryInfiniteQuery = useRetryApiInfiniteQuery();

  const playlists = query.state === "success" ? query.docs : [];
  const playlistIds = playlists.map((playlist) => playlist.id).join(",");
  // How many collections match the reader in all, every page included.
  const total = query.state === "success" ? query.total : null;
  if (total === undefined)
    throw new Error("should not happen: the ranked collections carry a total");

  return {
    playlists,
    playlistIds,
    total,
    // A failed request is not an empty answer: a page that can say so reads
    // it here (a failed "Load more" drops the loaded pages too).
    state: query.state,
    retry: () => {
      retryInfiniteQuery(query.key);
    },
    isLoading: query.state === "pending",
    isFetching: query.isFetching,
    fetchNextPage:
      query.state === "success" ? query.fetchNextPage : () => void 0,
    canFetchNextPage: query.state === "success" && query.canFetchNextPage,
  };
}
